import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { getUserAccessContext, resolveAuthenticatedHome } from "@/lib/access";
import { toast } from "sonner";
import { X, Lock, ArrowRight, Loader2, User, Sparkles, Apple, Eye, EyeOff, Mail, IdCard, Phone, KeyRound, Copy, Check, HelpCircle } from "lucide-react";
import DobPicker from "@/components/DobPicker";
import { useAuthModal } from "@/hooks/useAuthModal";
import { useAuth } from "@/hooks/useAuth";
import EmailOrPhoneInput, { type ContactMode } from "@/components/EmailOrPhoneInput";
import { PHONE_COUNTRY_CODES } from "@/data/phoneCountryCodes";
import BirthPlaceSelect from "@/components/BirthPlaceSelect";

const roles = [
  { value: "eleve", label: "Élève", emoji: "🎒", desc: "Primaire, Collège ou Lycée" },
  { value: "etudiant", label: "Étudiant", emoji: "🎓", desc: "Université" },
  { value: "enseignant", label: "Enseignant", emoji: "👨‍🏫", desc: "Professeur" },
  { value: "parent", label: "Parent", emoji: "👨‍👩‍👧", desc: "Parent d'élève" },
];

type IdentifierKind = "email" | "matricule" | "phone" | "unknown";

const detectIdentifier = (raw: string): IdentifierKind => {
  const v = raw.trim();
  if (!v) return "unknown";
  if (/^KEL-[A-Z0-9]{0,10}$/i.test(v)) return "matricule";
  if (/@/.test(v)) return "email";
  const cleaned = v.replace(/[\s\-().]/g, "");
  if (/^\+?[0-9]{6,20}$/.test(cleaned)) return "phone";
  return "unknown";
};

const AuthModal = () => {
  const { view, close, switchView } = useAuthModal();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Auto-close modal when user is authenticated
  useEffect(() => {
    if (user && view) {
      close();
    }
  }, [user, view, close]);

  // Login state
  const [loginEmail, setLoginEmail] = useState(""); // identifiant unifié (email/matricule/téléphone)
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [matriculeMode, setMatriculeMode] = useState(false);
  const [recoveredMatricule, setRecoveredMatricule] = useState<{ matricule: string; full_name: string | null } | null>(null);
  const [copiedMatricule, setCopiedMatricule] = useState(false);
  const [keepSession, setKeepSession] = useState(() => {
    try { return localStorage.getItem("kelasai_keep_session") !== "0"; } catch { return true; }
  });

  // Signup state
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupPhoneCountry, setSignupPhoneCountry] = useState(PHONE_COUNTRY_CODES[0]);
  const [signupContactMode, setSignupContactMode] = useState<ContactMode>("email");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  const [signupLastName, setSignupLastName] = useState("");
  const [signupFirstName, setSignupFirstName] = useState("");
  const [signupGender, setSignupGender] = useState("");
  const [signupDob, setSignupDob] = useState("");
  const [signupBirthPlace, setSignupBirthPlace] = useState("");
  const [signupRole, setSignupRole] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Google
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);

  // Reset form state when view changes
  useEffect(() => {
    if (!view) {
      setResetMode(false);
      setResetSent(false);
      setEmailSent(false);
      setMatriculeMode(false);
      setRecoveredMatricule(null);
      setCopiedMatricule(false);
    }
  }, [view]);

  if (!view) return null;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword.trim()) return;
    setLoginLoading(true);

    let resolvedEmail = loginEmail.trim();
    const kind = detectIdentifier(resolvedEmail);

    if (kind === "matricule") {
      if (!/^KEL-[A-Z0-9]{4,10}$/i.test(resolvedEmail)) {
        setLoginLoading(false);
        toast.error("Format matricule invalide (KEL-XXXXXX)");
        return;
      }
      const { data: resolved, error: resolveErr } = await supabase.functions.invoke("resolve-matricule-login", {
        body: { matricule: resolvedEmail, password: loginPassword },
      });
      if (resolveErr || !resolved?.session) {
        setLoginLoading(false);
        toast.error(resolved?.error || "Identifiant ou mot de passe incorrect");
        return;
      }
      const { data: setData, error: setErr } = await supabase.auth.setSession(resolved.session);
      setLoginLoading(false);
      try { localStorage.setItem("kelasai_keep_session", keepSession ? "1" : "0"); } catch {}
      if (setErr || !setData?.user) {
        toast.error(setErr?.message || "Connexion impossible");
        return;
      }
      toast.success("Connexion réussie !");
      close();
      const access = await getUserAccessContext(setData.user.id);
      navigate(resolveAuthenticatedHome(access));
      return;
    } else if (kind === "phone") {
      const cleaned = resolvedEmail.replace(/[\s\-().]/g, "");
      const { data: resolved, error: resolveErr } = await supabase.functions.invoke("resolve-matricule-login", {
        body: { phone: cleaned, password: loginPassword },
      });
      if (resolveErr || !resolved?.session) {
        setLoginLoading(false);
        toast.error(resolved?.error || "Identifiant ou mot de passe incorrect");
        return;
      }
      const { data: setData, error: setErr } = await supabase.auth.setSession(resolved.session);
      setLoginLoading(false);
      try { localStorage.setItem("kelasai_keep_session", keepSession ? "1" : "0"); } catch {}
      if (setErr || !setData?.user) {
        toast.error(setErr?.message || "Connexion impossible");
        return;
      }
      toast.success("Connexion réussie !");
      close();
      const access = await getUserAccessContext(setData.user.id);
      navigate(resolveAuthenticatedHome(access));
      return;
    } else if (kind === "unknown") {
      setLoginLoading(false);
      toast.error("Saisis un email, matricule ou numéro de téléphone valide");
      return;
    }

    const { data, error } = await supabase.auth.signInWithPassword({ email: resolvedEmail, password: loginPassword });
    setLoginLoading(false);

    try { localStorage.setItem("kelasai_keep_session", keepSession ? "1" : "0"); } catch {}

    if (error) {
      toast.error(error.message === "Invalid login credentials" ? "Identifiant ou mot de passe incorrect" : error.message);
      return;
    }

    toast.success("Connexion réussie !");
    close();

    if (data.user) {
      const access = await getUserAccessContext(data.user.id);
      navigate(resolveAuthenticatedHome(access));
      return;
    }
    navigate("/dashboard");
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail.trim()) {
      toast.error("Entre ton adresse email");
      return;
    }
    setLoginLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(loginEmail.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoginLoading(false);
    if (error) { toast.error(error.message); return; }
    setResetSent(true);
    toast.success("Email de réinitialisation envoyé ! 📧");
  };

  const handleMatriculeRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = loginEmail.trim();
    if (!value) {
      toast.error("Saisis ton email ou ton numéro de téléphone");
      return;
    }
    const kind = detectIdentifier(value);
    if (kind !== "email" && kind !== "phone") {
      toast.error("Saisis un email ou un numéro de téléphone valide");
      return;
    }
    setLoginLoading(true);
    const body = kind === "email"
      ? { email: value }
      : { phone: value.replace(/[\s\-().]/g, "") };
    const { data, error } = await supabase.functions.invoke("recover-matricule", { body });
    setLoginLoading(false);
    if (error || data?.error) {
      toast.error(data?.error || "Une erreur est survenue, réessaie plus tard");
      return;
    }
    toast.success(
      data?.message ||
        "Si un compte correspond à ces informations, ton matricule vient d'être envoyé par email 📧",
    );
    exitMatriculeMode();
  };

  const copyMatricule = async () => {
    if (!recoveredMatricule) return;
    await navigator.clipboard.writeText(recoveredMatricule.matricule);
    setCopiedMatricule(true);
    setTimeout(() => setCopiedMatricule(false), 2000);
  };

  const exitMatriculeMode = () => {
    setMatriculeMode(false);
    setRecoveredMatricule(null);
    setCopiedMatricule(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullName = `${signupLastName.trim()} ${signupFirstName.trim()}`.trim();
    const isPhone = signupContactMode === "phone";
    const contactValue = isPhone ? `${signupPhoneCountry.dialCode}${signupPhone}` : signupEmail.trim();

    if ((!isPhone && !signupEmail.trim()) || (isPhone && !signupPhone.trim()) || !signupPassword.trim() || !signupLastName.trim() || !signupFirstName.trim() || !signupRole) {
      if (!signupRole) toast.error("Sélectionne ton profil");
      return;
    }
    if (signupPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (signupPassword !== signupConfirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setSignupLoading(true);

    const signupOptions: any = {
      password: signupPassword,
      options: {
        data: {
          full_name: fullName,
          role: signupRole,
          gender: signupGender,
          date_of_birth: signupDob || null,
          birth_place: signupBirthPlace.trim() || null,
          phone: isPhone ? contactValue : null,
        },
        emailRedirectTo: window.location.origin,
      },
    };

    if (isPhone) {
      signupOptions.phone = contactValue;
    } else {
      signupOptions.email = contactValue;
    }

    const { error } = await supabase.auth.signUp(signupOptions);
    setSignupLoading(false);

    if (error) {
      if (error.message.includes("already registered")) {
        toast.error(isPhone ? "Ce numéro est déjà utilisé." : "Cet email est déjà utilisé. Essaie de te connecter.");
      } else {
        toast.error(error.message);
      }
      return;
    }

    setEmailSent(true);
    toast.success("Vérifie ta boîte mail pour confirmer ton compte ! 📧");
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    const { error } = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    setGoogleLoading(false);
    if (error) toast.error("Erreur de connexion Google");
  };

  const handleApple = async () => {
    setAppleLoading(true);
    const { error } = await lovable.auth.signInWithOAuth("apple", {
      redirect_uri: window.location.origin,
    });
    setAppleLoading(false);
    if (error) toast.error("Erreur de connexion Apple");
  };

  const googleButton = (
    <button type="button" disabled={googleLoading} onClick={handleGoogle}
      className="w-full py-3 rounded-2xl border-2 border-border bg-card hover:bg-muted transition-colors flex items-center justify-center gap-2.5 text-sm font-bold text-foreground">
      {googleLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <>
          <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Continuer avec Google
        </>
      )}
    </button>
  );

  const appleButton = (
    <button
      type="button"
      disabled={appleLoading}
      onClick={handleApple}
      className="w-full py-3 rounded-2xl border-2 border-border bg-card hover:bg-muted transition-colors flex items-center justify-center gap-2.5 text-sm font-bold text-foreground"
    >
      {appleLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Apple className="w-4 h-4" /> Continuer avec Apple</>}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={close} />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto rounded-3xl bg-card border-2 border-primary/50 p-6 animate-in fade-in zoom-in-95 duration-200" style={{ boxShadow: "0 0 20px hsl(var(--primary) / 0.3), 0 0 40px hsl(var(--primary) / 0.15), var(--shadow-md)" }}>
        {/* Close button */}
        <button onClick={close} className="absolute top-4 right-4 w-8 h-8 rounded-xl flex items-center justify-center bg-muted hover:bg-muted/80 transition-colors z-10">
          <X className="w-4 h-4 text-foreground" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--gradient-hero)" }}>
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-black text-gradient">KelasAI</span>
          </div>

          {view === "login" && !resetMode && !resetSent && (
            <>
              <h2 className="text-xl font-black text-foreground">
                {matriculeMode ? "Matricule oublié ? 🪪" : "Bon retour ! 👋"}
              </h2>
              <p className="text-sm text-muted-foreground font-medium mt-1">
                {matriculeMode
                  ? "Entre ton email ou ton téléphone pour retrouver ton code KEL-XXXXXX"
                  : "Connecte-toi pour continuer à apprendre"}
              </p>
            </>
          )}
          {view === "login" && resetMode && !resetSent && (
            <>
              <h2 className="text-xl font-black text-foreground">Mot de passe oublié ? 🔑</h2>
              <p className="text-sm text-muted-foreground font-medium mt-1">Entre ton email pour recevoir un lien</p>
            </>
          )}
          {view === "login" && resetSent && (
            <>
              <div className="text-5xl mb-2">📧</div>
              <h2 className="text-xl font-black text-foreground">Vérifie ta boîte mail</h2>
              <p className="text-sm text-muted-foreground font-medium mt-1">
                Un lien a été envoyé à <strong className="text-foreground">{loginEmail}</strong>
              </p>
            </>
          )}
          {view === "signup" && !emailSent && (
            <>
              <h2 className="text-xl font-black text-foreground">Créer un compte 🎓</h2>
              <p className="text-sm text-muted-foreground font-medium mt-1">Inscris-toi en 30 secondes. C'est gratuit !</p>
            </>
          )}
          {view === "signup" && emailSent && (
            <>
              <div className="text-5xl mb-2">📧</div>
              <h2 className="text-xl font-black text-foreground">Vérifie ta boîte mail !</h2>
              <p className="text-sm text-muted-foreground font-medium mt-1">
                Un email a été envoyé à <strong className="text-foreground">{signupEmail}</strong>
              </p>
            </>
          )}
        </div>

        {/* LOGIN FORM */}
        {view === "login" && !resetSent && !recoveredMatricule && (
          <form onSubmit={matriculeMode ? handleMatriculeRecovery : resetMode ? handlePasswordReset : handleLogin} className="space-y-4">
            {(() => {
              const kind = detectIdentifier(loginEmail);
              const Icon = matriculeMode
                ? (kind === "phone" ? Phone : Mail)
                : (kind === "matricule" ? IdCard : kind === "phone" ? Phone : Mail);
              const badge = matriculeMode
                ? (kind === "phone" ? { label: "Téléphone", color: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" }
                    : kind === "email" ? { label: "Email", color: "bg-blue-500/15 text-blue-600 dark:text-blue-400" }
                    : null)
                : (kind === "matricule" ? { label: "Matricule", color: "bg-primary/15 text-primary" }
                    : kind === "phone" ? { label: "Téléphone", color: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" }
                    : kind === "email" ? { label: "Email", color: "bg-blue-500/15 text-blue-600 dark:text-blue-400" }
                    : null);
              return (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-bold text-foreground">
                      {matriculeMode ? "Email ou téléphone" : resetMode ? "Email" : "Identifiant"}
                    </label>
                    {badge && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge.color}`}>
                        {badge.label}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 bg-muted rounded-2xl px-4 py-3">
                    <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                    <input
                      type="text"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder={matriculeMode
                        ? "ton@email.com ou +229 XX XX XX XX"
                        : resetMode
                          ? "ton@email.com"
                          : "Email, matricule (KEL-XXXXXX) ou téléphone"}
                      className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none"
                      required
                      autoComplete="username"
                    />
                  </div>
                  {!matriculeMode && !resetMode && (
                    <p className="text-[11px] text-muted-foreground mt-1.5 ml-1">
                      3 méthodes acceptées : <strong>email</strong>, <strong>matricule</strong> ou <strong>téléphone</strong>.
                    </p>
                  )}
                </div>
              );
            })()}

            {!resetMode && !matriculeMode && (
              <div>
                <label className="text-sm font-bold text-foreground mb-1.5 block">Mot de passe</label>
                <div className="flex items-center gap-3 bg-muted rounded-2xl px-4 py-3">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  <input type={showLoginPassword ? "text" : "password"} value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="••••••••" className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none" required minLength={6} />
                  <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)} className="p-1 hover:bg-muted-foreground/10 rounded-lg transition-colors">
                    {showLoginPassword ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-muted-foreground" />}
                  </button>
                </div>
                <div className="flex items-center justify-between mt-1.5 ml-1 gap-2">
                  <button type="button" onClick={() => setResetMode(true)} className="text-xs font-bold text-primary hover:underline">
                    Mot de passe oublié ?
                  </button>
                  <button type="button" onClick={() => setMatriculeMode(true)} className="text-xs font-bold text-primary hover:underline inline-flex items-center gap-1">
                    <HelpCircle className="w-3 h-3" /> Matricule oublié ?
                  </button>
                </div>
                <label className="flex items-center gap-2 mt-3 ml-1 cursor-pointer select-none group">
                  <input
                    type="checkbox"
                    checked={keepSession}
                    onChange={(e) => setKeepSession(e.target.checked)}
                    className="w-4 h-4 rounded border-2 border-border accent-primary cursor-pointer"
                  />
                  <span className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors">
                    Garder ma session ouverte
                  </span>
                </label>
                <p className="text-[10px] text-muted-foreground mt-1 ml-7 leading-snug">
                  {keepSession
                    ? "Tu resteras connecté sur cet appareil jusqu'à ce que tu te déconnectes."
                    : "Tu seras déconnecté automatiquement à la fermeture de l'onglet ou du navigateur."}
                </p>
              </div>
            )}

            <button type="submit" disabled={loginLoading} className="btn-hero w-full py-3 justify-center text-sm">
              {loginLoading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : matriculeMode
                  ? <><KeyRound className="w-4 h-4" /> Retrouver mon matricule</>
                  : resetMode
                    ? <><ArrowRight className="w-4 h-4" /> Envoyer le lien</>
                    : <><ArrowRight className="w-4 h-4" /> Se connecter</>}
            </button>

            {matriculeMode ? (
              <button type="button" onClick={exitMatriculeMode} className="w-full text-center text-sm text-primary font-bold hover:underline">
                Retour à la connexion
              </button>
            ) : resetMode ? (
              <button type="button" onClick={() => setResetMode(false)} className="w-full text-center text-sm text-primary font-bold hover:underline">
                Retour à la connexion
              </button>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs font-semibold text-muted-foreground">ou</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="space-y-3">
                  {googleButton}
                  {appleButton}
                </div>
                <p className="text-center text-sm text-muted-foreground font-medium">
                  Pas encore de compte ?{" "}
                  <button type="button" onClick={() => switchView("signup")} className="text-primary font-bold hover:underline">S'inscrire</button>
                </p>
              </>
            )}
          </form>
        )}

        {view === "login" && recoveredMatricule && (
          <div className="text-center">
            <div className="text-5xl mb-2">🎉</div>
            <h3 className="text-lg font-black text-foreground mb-1">Matricule retrouvé !</h3>
            {recoveredMatricule.full_name && (
              <p className="text-sm text-muted-foreground font-medium mb-3">
                Compte de <strong className="text-foreground">{recoveredMatricule.full_name}</strong>
              </p>
            )}
            <div className="bg-muted rounded-2xl p-4 mb-4 border-2 border-primary/30">
              <p className="text-xs text-muted-foreground font-semibold mb-1">Ton matricule KelasAI</p>
              <p className="text-2xl font-black text-foreground font-mono tracking-widest">{recoveredMatricule.matricule}</p>
            </div>
            <div className="space-y-2">
              <button onClick={copyMatricule} className="btn-hero w-full py-3 text-sm justify-center">
                {copiedMatricule ? <><Check className="w-4 h-4" /> Copié !</> : <><Copy className="w-4 h-4" /> Copier le matricule</>}
              </button>
              <button onClick={exitMatriculeMode} className="w-full text-center text-sm text-primary font-bold hover:underline py-2">
                Retour à la connexion
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              Conserve ce matricule en lieu sûr. Tu pourras t'en servir pour te connecter à la place de ton email.
            </p>
          </div>
        )}

        {view === "login" && resetSent && (
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-4">Si tu ne le vois pas, vérifie tes spams.</p>
            <button onClick={() => { setResetMode(false); setResetSent(false); }} className="btn-hero inline-flex py-3 px-6 text-sm">
              <ArrowRight className="w-4 h-4" /> Retour à la connexion
            </button>
          </div>
        )}

        {/* SIGNUP FORM */}
        {view === "signup" && !emailSent && (
          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="text-sm font-bold text-foreground mb-2 block">Je suis...</label>
              <div className="grid grid-cols-2 gap-2">
                {roles.map((r) => (
                  <button key={r.value} type="button" onClick={() => setSignupRole(r.value)}
                    className={`p-2.5 rounded-2xl border-2 text-left transition-all flex items-center gap-2 ${signupRole === r.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
                    <span className="text-lg">{r.emoji}</span>
                    <div>
                      <p className="font-bold text-xs text-foreground">{r.label}</p>
                      <p className="text-[10px] text-muted-foreground">{r.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-bold text-foreground mb-1.5 block">Nom</label>
              <div className="flex items-center gap-3 bg-muted rounded-2xl px-4 py-3">
                <User className="w-4 h-4 text-muted-foreground" />
                <input type="text" value={signupLastName} onChange={(e) => setSignupLastName(e.target.value)} placeholder="Mon nom de famille"
                  className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none" required />
              </div>
            </div>

            <div>
              <label className="text-sm font-bold text-foreground mb-1.5 block">Prénom</label>
              <div className="flex items-center gap-3 bg-muted rounded-2xl px-4 py-3">
                <User className="w-4 h-4 text-muted-foreground" />
                <input type="text" value={signupFirstName} onChange={(e) => setSignupFirstName(e.target.value)} placeholder="Mon prénom"
                  className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none" required />
              </div>
            </div>

            <div>
              <label className="text-sm font-bold text-foreground mb-1.5 block">Je suis</label>
              <div className="grid grid-cols-2 gap-2">
                {(signupRole === "parent" || signupRole === "enseignant" ? [
                  { value: "M", label: "Homme", emoji: "👨" },
                  { value: "F", label: "Femme", emoji: "👩" },
                ] : [
                  { value: "M", label: "Garçon", emoji: "👦" },
                  { value: "F", label: "Fille", emoji: "👧" },
                ]).map((g) => (
                  <button key={g.value} type="button" onClick={() => setSignupGender(g.value)}
                    className={`p-2.5 rounded-2xl border-2 text-center transition-all text-sm font-bold ${signupGender === g.value ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/30 text-foreground"}`}>
                    {g.emoji} {g.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-bold text-foreground mb-1.5 block">Je suis né(e) le</label>
              <DobPicker value={signupDob} onChange={setSignupDob} />
            </div>

            <div>
              <label className="text-sm font-bold text-foreground mb-1.5 block">Je suis né(e) à</label>
              <BirthPlaceSelect value={signupBirthPlace} onChange={setSignupBirthPlace} />
            </div>

            <EmailOrPhoneInput
              mode={signupContactMode}
              onModeChange={setSignupContactMode}
              emailValue={signupEmail}
              onEmailChange={setSignupEmail}
              phoneValue={signupPhone}
              onPhoneChange={setSignupPhone}
              phoneCountry={signupPhoneCountry}
              onPhoneCountryChange={setSignupPhoneCountry}
            />

            <div>
              <label className="text-sm font-bold text-foreground mb-1.5 block">Mot de passe</label>
              <div className="flex items-center gap-3 bg-muted rounded-2xl px-4 py-3">
                <Lock className="w-4 h-4 text-muted-foreground" />
                <input type={showSignupPassword ? "text" : "password"} value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} placeholder="••••••••"
                  className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none" required minLength={6} />
                <button type="button" onClick={() => setShowSignupPassword(!showSignupPassword)} className="p-1 hover:bg-muted-foreground/10 rounded-lg transition-colors">
                  {showSignupPassword ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-muted-foreground" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5 ml-1">Minimum 6 caractères</p>
            </div>

            <div>
              <label className="text-sm font-bold text-foreground mb-1.5 block">Confirmer le mot de passe</label>
              <div className="flex items-center gap-3 bg-muted rounded-2xl px-4 py-3">
                <Lock className="w-4 h-4 text-muted-foreground" />
                <input type={showSignupConfirmPassword ? "text" : "password"} value={signupConfirmPassword} onChange={(e) => setSignupConfirmPassword(e.target.value)} placeholder="••••••••"
                  className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none" required minLength={6} />
                <button type="button" onClick={() => setShowSignupConfirmPassword(!showSignupConfirmPassword)} className="p-1 hover:bg-muted-foreground/10 rounded-lg transition-colors">
                  {showSignupConfirmPassword ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-muted-foreground" />}
                </button>
              </div>
              {signupConfirmPassword && signupPassword !== signupConfirmPassword && (
                <p className="text-xs text-destructive mt-1.5 ml-1">Les mots de passe ne correspondent pas</p>
              )}
            </div>

            <button type="submit" disabled={signupLoading} className="btn-hero w-full py-3 justify-center text-sm">
              {signupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> Créer mon compte</>}
            </button>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs font-semibold text-muted-foreground">ou</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <div className="space-y-3">
              {googleButton}
              {appleButton}
            </div>

            <p className="text-center text-sm text-muted-foreground font-medium">
              Déjà un compte ?{" "}
              <button type="button" onClick={() => switchView("login")} className="text-primary font-bold hover:underline">Se connecter</button>
            </p>
          </form>
        )}

        {view === "signup" && emailSent && (
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-4">Clique sur le lien dans l'email pour activer ton compte. Vérifie tes spams si besoin.</p>
            <button onClick={() => { switchView("login"); setEmailSent(false); }} className="btn-hero inline-flex py-3 px-6 text-sm">
              <ArrowRight className="w-4 h-4" /> Aller à la connexion
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthModal;